import React from 'react';
import styled from 'styled-components';

const ConfirmationContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
  background: linear-gradient(180deg, #ffffff, #fafafa);
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 12px;
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem 1rem;
    border-radius: 10px;
    margin: 0 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem;
  }
  
  @media print {
    display: none !important;
  }
`;

const SuccessIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #2ecc71, #27ae60);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 3rem;
  font-weight: bold;
  box-shadow: 0 8px 24px rgba(46, 204, 113, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    border: 2px solid rgba(46, 204, 113, 0.2);
    animation: pulse 2s ease-out infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
  
  @media (max-width: 576px) {
    width: 80px;
    height: 80px;
    font-size: 2.5rem;
    margin-bottom: 1.25rem;
    
    &::after {
      width: 96px;
      height: 96px;
    }
  }
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  font-weight: 700;
  
  @media (max-width: 576px) {
    font-size: 1.75rem;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1.15rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2.5rem;
  line-height: 1.7;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const MembershipReference = styled.div`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  margin: 2rem 0;
  font-weight: 600;
  
  @media (max-width: 576px) {
    padding: 1.25rem 1.5rem;
  }
`;

const ReferenceLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReferenceNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
  
  @media (max-width: 576px) {
    font-size: 1.25rem;
  }
`;

const SavingsSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  border: 2px solid ${props => props.theme.colors.accent};
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin: 1.5rem 0;
  }
  
  @media (max-width: 576px) {
    padding: 1rem;
    margin: 1.25rem 0;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
    margin: 1rem 0;
  }
`;

const SavingsTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const SavingsAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin: 0.5rem 0;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
    margin: 0.375rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.35rem;
  }
`;

const SavingsText = styled.p`
  color: ${props => props.theme.colors.secondary};
  margin: 0.5rem 0 0 0;
  font-size: 0.95rem;
`;

const MembershipDetails = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  
  @media (max-width: 576px) {
    padding: 1.25rem;
    margin: 1.25rem 0;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin: 1rem 0;
  }
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.colors.accent};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 0.625rem 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
`;

const DetailValue = styled.span`
  color: ${props => props.theme.colors.primary};
  text-align: right;
  
  @media (max-width: 576px) {
    text-align: left;
    word-break: break-word;
  }
`;

const MembershipsList = styled.div`
  margin-top: 1.5rem;
`;

const MembershipItem = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  border-left: 4px solid ${props => props.theme.colors.accent};
  
  @media (max-width: 576px) {
    padding: 1rem;
    margin-bottom: 0.875rem;
    border-left-width: 3px;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 6px;
  }
`;

const MembershipName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const MembershipQuantity = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 0.95rem;
`;

const PriceSection = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  color: white;
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  
  @media (max-width: 576px) {
    padding: 1.25rem;
    margin: 1.25rem 0;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin: 1rem 0;
  }
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.75rem 0;
  font-size: 1.1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  &:last-child {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 2px solid rgba(255,255,255,0.3);
    font-size: 1.5rem;
    font-weight: 700;
    
    @media (max-width: 576px) {
      font-size: 1.25rem;
      margin-top: 1.25rem;
      padding-top: 1.25rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.15rem;
    }
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    flex-direction: column;
    align-items: flex-start;
    margin: 0.625rem 0;
  }
`;

const PriceLabel = styled.span`
  opacity: 0.9;
`;

const PriceValue = styled.span`
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 2.5rem 0;
  flex-wrap: wrap;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: ${props => props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  @media (max-width: 576px) {
    width: 100%;
    padding: 1rem;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2.5rem;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
    border-radius: 12px;
    z-index: -1;
    opacity: 0.1;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 2rem;
  }
  
  @media (max-width: 576px) {
    padding: 1.25rem;
    margin-top: 1.5rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 1.25rem;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  font-weight: 500;
`;

const MembershipConfirmation = ({ 
  bookingReference, 
  selectedTickets, 
  customerDetails, 
  totalAmount,
  venposBasket,
  bookingSummary
}) => {
  // Extract discount information
  const basketData = venposBasket?.data;
  const discounts = basketData?.discounts || bookingSummary?.discounts || [];
  const totalSavings = bookingSummary?.totalSavings || discounts.reduce((sum, discount) => sum + (discount.discountedAmount || 0), 0);
  const hasSavings = totalSavings > 0;
  
  // Calculate prices
  let originalPrice, finalPrice;
  if (bookingSummary?.originalPrice && bookingSummary?.finalPrice !== undefined) {
    originalPrice = bookingSummary.originalPrice;
    finalPrice = bookingSummary.finalPrice;
  } else if (basketData?.totalPrice && basketData?.finalPrice !== undefined) {
    originalPrice = basketData.totalPrice;
    finalPrice = basketData.finalPrice;
  } else {
    originalPrice = totalAmount;
    finalPrice = totalAmount;
  }
  
  const savingsPercentage = originalPrice > 0 ? ((totalSavings / originalPrice) * 100).toFixed(0) : 0;
  
  // Get membership items from basket or selectedTickets
  const basketGroups = basketData?.groups || [];
  const basketItems = basketGroups.flatMap(g => g.items || []);
  
  const membershipsToDisplay = basketItems.length > 0 
    ? (() => {
        const groupedMap = new Map();
        
        basketItems.forEach(item => {
          const key = `${item.webSaleItemId}-${item.name}`;
          const itemQuantity = item.quantity || 1;
          const unitPrice = item.unitPrice || (item.totalPrice / itemQuantity) || item.price || 0;
          
          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key);
            existing.quantity += itemQuantity;
            existing.totalPrice += item.totalPrice || (unitPrice * itemQuantity);
          } else {
            groupedMap.set(key, {
              webSaleItemId: item.webSaleItemId,
              name: item.name || item.description,
              quantity: itemQuantity,
              unitPrice: unitPrice,
              totalPrice: item.totalPrice || (unitPrice * itemQuantity)
            });
          }
        });
        
        return Array.from(groupedMap.values());
      })()
    : (() => {
        const groupedMap = new Map();
        
        selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {
          const key = `${ticket.webSaleItemId}-${ticket.name}`;
          
          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key);
            existing.quantity += ticket.quantity;
            existing.totalPrice += ticket.price * ticket.quantity;
          } else {
            groupedMap.set(key, {
              webSaleItemId: ticket.webSaleItemId,
              name: ticket.name,
              quantity: ticket.quantity,
              unitPrice: ticket.price,
              totalPrice: ticket.price * ticket.quantity
            });
          }
        });
        
        return Array.from(groupedMap.values());
      })();

  const handleReturnHome = () => {
    window.location.href = '/';
  };

  return (
    <ConfirmationContainer className="no-print">
      <SuccessIcon>
        ✓
      </SuccessIcon>
      
      <SuccessTitle>Membership Confirmed!</SuccessTitle>
      <SuccessMessage>
        Thank you for your membership purchase. Your membership to Belvoir Castle has been confirmed.
        A confirmation email has been sent to {customerDetails?.email || 'your email address'}.
      </SuccessMessage>

      <MembershipReference>
        <ReferenceLabel>Membership Reference</ReferenceLabel>
        <ReferenceNumber>{bookingReference}</ReferenceNumber>
      </MembershipReference>

      {hasSavings && (
        <SavingsSection>
          <SavingsTitle>Special Offer</SavingsTitle>
          <SavingsAmount>£{totalSavings.toFixed(2)}</SavingsAmount>
          <SavingsText>
            You saved {savingsPercentage}% on your membership
          </SavingsText>
        </SavingsSection>
      )}

      <MembershipDetails>
        <SectionTitle>Membership Details</SectionTitle>
        
        <DetailRow>
          <DetailLabel>Total Memberships</DetailLabel>
          <DetailValue>{membershipsToDisplay.reduce((sum, m) => sum + m.quantity, 0)}</DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Contact Information</DetailLabel>
          <DetailValue></DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Name</DetailLabel>
          <DetailValue>
            {customerDetails?.firstName} {customerDetails?.lastName}
          </DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Email</DetailLabel>
          <DetailValue>{customerDetails?.email}</DetailValue>
        </DetailRow>

        {customerDetails?.phone && (
          <DetailRow>
            <DetailLabel>Phone</DetailLabel>
            <DetailValue>{customerDetails.phone}</DetailValue>
          </DetailRow>
        )}

        <MembershipsList>
          <SectionTitle style={{ fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>
            Memberships
          </SectionTitle>
          {membershipsToDisplay.map((membership, index) => (
            <MembershipItem key={membership.webSaleItemId || index}>
              <MembershipName>{membership.name}</MembershipName>
              <MembershipQuantity>
                {membership.quantity} x £{membership.unitPrice?.toFixed(2) || '0.00'} = £{membership.totalPrice?.toFixed(2) || '0.00'}
              </MembershipQuantity>
            </MembershipItem>
          ))}
        </MembershipsList>
      </MembershipDetails>

      <PriceSection>
        <PriceRow>
          <PriceLabel>Original Price</PriceLabel>
          <PriceValue>£{originalPrice.toFixed(2)}</PriceValue>
        </PriceRow>
        {hasSavings && (
          <PriceRow>
            <PriceLabel>Discount</PriceLabel>
            <PriceValue>-£{totalSavings.toFixed(2)}</PriceValue>
          </PriceRow>
        )}
        <PriceRow>
          <PriceLabel>Total Paid (inc. VAT)</PriceLabel>
          <PriceValue>£{finalPrice.toFixed(2)}</PriceValue>
        </PriceRow>
      </PriceSection>

      <ActionButtons>
        <SecondaryButton onClick={handleReturnHome}>
          Return Home
        </SecondaryButton>
      </ActionButtons>

      <InfoBox>
        <InfoText>
          Please check your email for your membership confirmation and details. Your membership card and further information will be sent to you separately.
        </InfoText>
      </InfoBox>
    </ConfirmationContainer>
  );
};

export default MembershipConfirmation;

